import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { EMPTY_ASSISTANT_RESPONSE_MESSAGE } from "../constants/chatConstants";
import type {
  ManagingPromptCatalogEntry,
  SuggestedPromptSummary,
} from "../types/chat.types";

export interface MessageSuggestedPromptsProps {
  suggestions: SuggestedPromptSummary[];
  promptCatalog?: ManagingPromptCatalogEntry[];
  onSelect: (prompt: ManagingPromptCatalogEntry) => void;
  messageContent?: string;
}

function resolveCatalogEntry(
  summary: SuggestedPromptSummary,
  catalog: ManagingPromptCatalogEntry[] | undefined
): ManagingPromptCatalogEntry {
  const match = catalog?.find((p) => p.promptType === summary.promptType);
  if (match) return match;
  return {
    promptType: summary.promptType,
    name: summary.name,
    description: summary.description ?? "",
    categories: [],
  };
}

const MessageSuggestedPrompts: React.FC<MessageSuggestedPromptsProps> = ({
  suggestions,
  promptCatalog,
  onSelect,
  messageContent,
}) => {
  const { t } = useTranslation();

  const visible = useMemo(() => {
    if (!suggestions.length) return false;
    if (!messageContent?.trim()) return true;
    return messageContent.trim() === EMPTY_ASSISTANT_RESPONSE_MESSAGE;
  }, [messageContent, suggestions.length]);

  if (!visible) return null;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <Sparkles className="h-3.5 w-3.5 text-blue-600" aria-hidden />
        {t("Try a suggested prompt")}
      </p>
      <ul className="flex flex-col gap-1.5" role="list">
        {suggestions.map((item) => (
          <li key={item.promptType}>
            <button
              type="button"
              onClick={() => onSelect(resolveCatalogEntry(item, promptCatalog))}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm text-gray-900 transition hover:border-blue-300 hover:bg-blue-50"
            >
              <span className="font-medium">{item.name}</span>
              {item.description ? (
                <span className="mt-0.5 block text-xs text-gray-500 line-clamp-2">
                  {item.description}
                </span>
              ) : null}
              {item.setupReason ? (
                <span className="mt-1 block text-[11px] text-amber-700">{item.setupReason}</span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MessageSuggestedPrompts;
